import type { Media_CreateMediaItem_Mutation$data } from '@/graphql/mutations/Media.mutation';
import { MimeTypeEnum } from '@/graphql/types';
import { Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FileUploaderWithPreview } from '../FileUploader';

import './DragDropWrapper.less';

export type AllowedTypes =
  | 'text/plain'
  | 'image/png'
  | 'image/jpeg'
  | 'image/webp'
  | 'image/gif'
  | 'video/webm'
  | 'video/mp4'
  | 'audio/mp3'
  | 'application/pdf';

const DragDropWrapper = ({
  children,
  mimeTypes = ['text/plain'],
  outlineOnly = false,
  disabled = false,
  onChange,
  fileCallback,
  stringCheck,
  unsupportedMessage = 'File type not supported here',
  infoMessage = 'Drop file here',
  invalidStringMessage = 'This is an invalid input',
  style,
}: {
  children: React.ReactNode;
  mimeTypes?: AllowedTypes[];
  outlineOnly?: boolean;
  disabled?: boolean;
  onChange?: (value: string | File) => void;
  fileCallback?: (data: Media_CreateMediaItem_Mutation$data | null) => void;
  stringCheck?: (data: string) => boolean;
  unsupportedMessage?: string;
  infoMessage?: string;
  invalidStringMessage?: string;
  style?: React.CSSProperties;
}) => {
  const [dragHover, setDragHover] = useState<boolean>(false);
  const [tooltipStatus, setTooltipStatus] = useState<'inactive' | 'warn' | 'info'>('inactive');
  const [tooltipMessage, setTooltipMessage] = useState<string>(unsupportedMessage);
  const [tooltipColor, setTooltipColor] = useState<string>('#c72020');
  const [file, setFile] = useState<File | null>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (outlineOnly || disabled) return;
    e.preventDefault();
    setDragHover(true);
    e.dataTransfer.dropEffect = 'move';
    // If incorrect mime type
    if (!mimeTypes.includes(e.dataTransfer?.items[0].type as AllowedTypes)) setTooltipStatus('warn');
    else setTooltipStatus('info');
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (outlineOnly || disabled) return;
    e.preventDefault();
    resetState();
    const mimeType = e.dataTransfer.items[0].type;
    // If correct mime type
    if (!!e.dataTransfer.items[0] && mimeTypes.includes(mimeType as AllowedTypes)) {
      if (mimeType === 'text/plain') {
        e.dataTransfer.items[0].getAsString((text) => {
          if ((!!stringCheck && stringCheck(text as string)) || !stringCheck) onChange?.(text);
          else toast.error(invalidStringMessage);
        });
      }
      // Priority to file uploader
      else if (!!fileCallback) {
        setFile(e.dataTransfer.items[0].getAsFile());
      } else {
        onChange?.(e?.dataTransfer?.items?.[0]?.getAsFile() ?? '');
      }
    }
  };

  const resetState = () => {
    setDragHover(false);
    setTooltipStatus('inactive');
  };

  useEffect(() => {
    if (tooltipStatus === 'warn') {
      setTooltipMessage(unsupportedMessage);
      setTooltipColor('#c72020');
    } else if (tooltipStatus === 'info') {
      setTooltipMessage(infoMessage);
      setTooltipColor('#4287f5');
    }
  }, [tooltipStatus]);

  return (
    <>
      {!!file && (
        <FileUploaderWithPreview
          file={file}
          setFile={setFile}
          callback={(data) => {
            fileCallback?.(data);
          }}
          mimeTypes={[
            MimeTypeEnum.ImagePng,
            MimeTypeEnum.ImageGif,
            MimeTypeEnum.ImageJpeg,
            MimeTypeEnum.VideoMp4,
            MimeTypeEnum.VideoWebm,
          ]}
        />
      )}
      <Tooltip
        open={tooltipStatus !== 'inactive' && !outlineOnly}
        title={tooltipMessage}
        placement="top"
        slotProps={{ tooltip: { sx: { backgroundColor: tooltipColor } } }}
      >
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={() => {
            resetState();
          }}
          className={`drag-drop-wrapper-container ${dragHover && !disabled ? 'drag-hover' : ''} ${
            tooltipStatus === 'warn' && !disabled ? 'drag-drop-unsupported' : ''
          }`}
          style={style}
        >
          {children}
        </div>
      </Tooltip>
    </>
  );
};

export default DragDropWrapper;
